import { useCallback, useEffect, useRef } from 'react';

import { Tooltip } from '@material-ui/core';

import { RiChat1Line } from '@remixicon/react';

import { useCommentState } from '@spotify/plugin-confidence-comments-react';

import { useCurrentEditor } from '../Context';
import { ToolbarButton } from '../ToolbarButton';

type CommentButtonProps = {
  isBlockComment?: boolean;
};

const generateCommentId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `tiptap-comment-${crypto.randomUUID()}`;
  }

  // fallback
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 11);
  return `tiptap-comment-${timestamp}-${random}`;
};

export const CommentButton = ({
  isBlockComment = false,
}: CommentButtonProps) => {
  const { editor } = useCurrentEditor();
  const comment = useCommentState(state => state.comment);
  const creatingAnchor = useCommentState(state => state.creatingAnchor);
  const addListener = useCommentState(state => state.addListener);
  const pendingCommentIdRef = useRef<string | null>(null);

  const handleClick = useCallback(() => {
    if (!editor) return;

    const commentId = generateCommentId();

    // Add mark immediately so it's in Yjs document from the start
    // The mark provides both persistence (Yjs sync) and visual highlighting (CSS)
    editor.chain().focus().setComment(commentId).run();

    pendingCommentIdRef.current = commentId;

    comment({ zone: commentId });
  }, [editor, comment]);

  useEffect(() => {
    const removeListener = addListener(({ anchor, reason }) => {
      if (reason === 'created' && anchor?.zone) {
        if (pendingCommentIdRef.current === anchor.zone) {
          // Comment was successfully created, clear pending
          pendingCommentIdRef.current = null;
        }
      }
    });

    return removeListener;
  }, [addListener]);

  // Watch for comment cancellation
  useEffect(() => {
    if (!creatingAnchor && pendingCommentIdRef.current && editor) {
      editor.commands.removeCommentById(pendingCommentIdRef.current);
      pendingCommentIdRef.current = null;
    }
  }, [creatingAnchor, editor]);

  if (!editor) return null;

  return (
    <Tooltip title={isBlockComment ? 'Comment on block' : 'Add comment'}>
      <ToolbarButton
        aria-label={isBlockComment ? 'Comment on block' : 'Add comment'}
        onClick={handleClick}
        data-track-id="button.add-comment"
        data-track-action="create"
      >
        <RiChat1Line />
      </ToolbarButton>
    </Tooltip>
  );
};
